import React from "react";
import { Menu, Transition } from "@headlessui/react";
import { cn } from "@/utils/shadcn";
import {
  TAX_YEARS,
  CURRENT_TAX_YEAR,
  useProfile,
  isUsJurisdiction,
} from "@moonscape/shared";
import { useTaxYearOptional } from "@/screens/TaxScreen/contexts";
import { LAB, NUM } from "./tokens";
import { CheckMark, Chevron, Lab } from "./atoms";

type YearOption = { value: number; taxYear: string };

/**
 * Header tax-year dropdown. Reads and writes the real TaxYearContext (falls back
 * to the optional hook so it can also render outside a provider).
 *
 * `statuses` is the per-year sub-line ("Audit-ready · £4,182.60"). Screens that
 * know a year's state can pass it in; without it the rows are just years.
 */
export const TaxYearPicker = ({
  statuses,
  className,
}: {
  statuses?: Record<number, React.ReactNode>;
  className?: string;
}) => {
  const { selectedTaxYear, setSelectedTaxYear } = useTaxYearOptional();
  const { profile } = useProfile();

  const isUS = isUsJurisdiction(profile?.taxJurisdiction);
  const authority = isUS ? "IRS" : "HMRC";
  const period = isUS ? "US · 1 Jan – 31 Dec" : "UK · 6 Apr – 5 Apr";

  const years: YearOption[] = TAX_YEARS || [];
  const closed = years.filter((y) => y.value < CURRENT_TAX_YEAR);
  const inProgress = years.filter((y) => y.value >= CURRENT_TAX_YEAR);

  const label =
    years.find((y) => y.value === selectedTaxYear)?.taxYear || String(selectedTaxYear);

  // one year to choose from is not a choice — render the plain label
  if (years.length < 2) {
    return (
      <span
        className={cn(
          "inline-flex min-h-[38px] items-center gap-2 border border-ms-line bg-ms-card px-[11px]",
          className
        )}
      >
        <Lab size={10} className="text-ms-ink">
          {label}
        </Lab>
        <Lab size={10} className="hidden text-ms-dim ms1100:inline">
          {authority}
        </Lab>
      </span>
    );
  }

  const Row = ({ y }: { y: YearOption }) => {
    const on = y.value === selectedTaxYear;
    return (
      <Menu.Item>
        {({ active }) => (
          <button
            type="button"
            onClick={() => setSelectedTaxYear(y.value)}
            className={cn(
              "flex w-full items-center gap-2.5 border-b border-ms-line px-3.5 py-3 text-left text-ms-ink last:border-b-0",
              (active || on) && "bg-ms-card2",
              on && "bg-ms-mint-soft"
            )}
          >
            <span className={cn(NUM, "w-[74px] flex-none text-[13.5px] font-semibold")}>
              {y.taxYear}
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 text-[11.5px]",
                on ? "text-ms-link" : "text-ms-mute"
              )}
            >
              {statuses?.[y.value] ?? ""}
            </span>
            <span className="grid w-3.5 flex-none place-items-center text-ms-green">
              {on ? <CheckMark /> : null}
            </span>
          </button>
        )}
      </Menu.Item>
    );
  };

  return (
    <Menu as="div" className={cn("relative inline-flex", className)}>
      <Menu.Button
        className={cn(
          "inline-flex min-h-[38px] items-center gap-2 border border-ms-line bg-ms-card px-[11px] text-ms-mute transition-colors hover:border-ms-line2"
        )}
      >
        <span className={cn(LAB, "text-[10px] text-ms-ink")}>{label}</span>
        <span className={cn(LAB, "hidden text-[10px] text-ms-dim ms1100:inline")}>
          {authority}
        </span>
        <Chevron />
      </Menu.Button>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 top-[calc(100%+6px)] z-[60] max-h-[70vh] min-w-[268px] overflow-y-auto border border-t-[3px] border-ms-line border-t-ms-rule bg-ms-card shadow-[0_10px_30px_rgba(20,24,26,.13)] focus:outline-none">
          <div className="flex items-baseline justify-between gap-2.5 border-b border-ms-line px-3.5 py-[11px]">
            <Lab>Tax year</Lab>
            <Lab className="text-ms-dim">{period}</Lab>
          </div>
          {closed.map((y) => (
            <Row key={y.value} y={y} />
          ))}
          {inProgress.length > 0 && (
            <>
              <div className="border-b border-t border-ms-line bg-ms-card2 px-3.5 py-[11px]">
                <Lab className="text-ms-dim">Current year in progress</Lab>
              </div>
              {inProgress.map((y) => (
                <Row key={y.value} y={y} />
              ))}
            </>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
